import {Table} from 'dexie';
import {IDatabase} from '@src/@interfaces/services/Database';
import app from '@src/helpers/app';
import {IModel} from '@src/@interfaces/services/Database/IModel';
import ModelNotFoundException from '@src/exceptions/ModelNotFoundException';

type ModelAttributes<I extends IModel> = Partial<NonNullable<Record<keyof I, I[keyof I]>>>;

export default abstract class AbstractModel<I extends IModel, PK = number> {
    readonly id!: number;

    async find(id: PK): Promise<I | undefined> {
        return this.query().get(id);
    }

    async findOrFail(id: PK) {
        const model = await this.find(id);
        if (!model) {
            throw new ModelNotFoundException('Exceptions.model_not_found');
        }
    }

    async update(attributes: ModelAttributes<I>) {
        this.fill(attributes);

        return this.save();
    }

    async save() {
        return this.query().put(this as any);
    }

    fill(attributes: ModelAttributes<I>): void {
        Object.assign(this, attributes);
    }

    protected query() {
        return this.table;
    }

    protected abstract get table(): Table<I, any>;

    protected get connection(): IDatabase {
        return app('db');
    }

    static query<Model = any>(): Table<Model, any> {
        return this.prototype.query();
    }

    static async updateOrCreate<Model extends IModel = any>(attributes: ModelAttributes<Model>, values?: ModelAttributes<Model>): Promise<any> {
        return this.prototype.query().get(attributes).then(model => {
            if (!model || !(model instanceof AbstractModel)) {
                throw new Error();
            }

            model.fill(values ?? {});

            return model.save();
        }).catch(() => {
            return this.prototype.query().put(Object.assign({}, attributes, values));
        });
    }

    static async updateOrCreateMany<Model extends IModel = any>(records: [attributes: ModelAttributes<Model>, values?: ModelAttributes<Model>][]): Promise<any> {
        return this.prototype.connection.transaction('rw?', this.prototype.query(), async () => {
            const queue = [];
            for (const record of records) {
                queue.push(
                    this.updateOrCreate<Model>(...record)
                );
            }

            await Promise.all(queue);
        });
    }
}
